import { makePostApiCall } from "./makeApiCall";
import { dropdownForPayload } from "./locationDropdowns";

export const PACKAGING_STATUS_IN_STOCK = 0;

export function getPackagingRowStatus(row) {
  const raw = row?.status ?? row?.statustatus;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function getPackagingBarcode(row) {
  return row?.barcode ?? row?.shipping_barcode ?? null;
}

export function mapLookupItemToTransferRecord(item, itemType) {
  return {
    id: item?.id,
    type: itemType,
    barcode: getPackagingBarcode(item),
    serial_number: item?.serial_number,
    product_name: item?.product_name,
  };
}

export function isPackagingItemTransferable(item, itemType) {
  if (!item?.id || !itemType) {
    return false;
  }
  const barcode = getPackagingBarcode(item);
  if (!barcode) {
    return false;
  }
  return getPackagingRowStatus(item) === PACKAGING_STATUS_IN_STOCK;
}

export function isRowSelectable(row) {
  if (!row?.id || !row?.barcode || !row?.carton_type) {
    return false;
  }
  return getPackagingRowStatus(row) === PACKAGING_STATUS_IN_STOCK;
}

export function mapRowToBulkRecord(row) {
  return {
    id: row.id,
    type: row.carton_type,
    barcode: row.barcode,
    serial_number: row.serial_number,
    product_name: row.product_name,
  };
}

export function dedupeSelectedPackagingRows(rows) {
  const selectedPalletIds = new Set(
    rows.filter((row) => row.carton_type === "pallet").map((row) => row.id),
  );

  return rows.filter((row) => {
    if (
      row.carton_type === "carton" &&
      row.pallet_id &&
      selectedPalletIds.has(row.pallet_id)
    ) {
      return false;
    }
    return true;
  });
}

export function getPackagingLocationId(row) {
  if (!row) {
    return null;
  }
  const raw = row.location_id ?? row.location?.value;
  if (raw == null || raw === "") {
    return null;
  }
  return raw;
}

export function getPackagingZoneId(row) {
  if (!row) {
    return null;
  }
  const raw = row.zone_id ?? row.zone?.value;
  if (raw == null || raw === "") {
    return null;
  }
  return raw;
}

export function getPackagingBrandId(row) {
  if (!row) {
    return null;
  }
  const raw = row.brand_id ?? row.brand?.value;
  if (raw == null || raw === "") {
    return null;
  }
  return raw;
}

export function getPackagingGroupKey(row) {
  if (!row) {
    return "";
  }
  return [
    row.plu,
    row.lot_number,
    normalizePackagingDateForApi(row.packaging_date),
    getPackagingBrandId(row) ?? "",
    getPackagingLocationId(row) ?? "",
    getPackagingZoneId(row) ?? "",
  ].join("|");
}

export function normalizePackagingDateForApi(value) {
  if (value == null || value === "") {
    return value;
  }

  const str = String(value).trim();
  if (str.length >= 10) {
    return str.slice(0, 10);
  }

  return str;
}

export function buildGetPackagingSubItemsPayload(row) {
  const payload = {
    plu: row.plu,
    lot_number: row.lot_number,
    packaging_date: normalizePackagingDateForApi(row.packaging_date),
  };

  const locationId = getPackagingLocationId(row);
  const zoneId = getPackagingZoneId(row);
  const brandId = getPackagingBrandId(row);

  if (brandId != null) {
    payload.brand_id = Number(brandId);
  }
  if (locationId != null) {
    payload.location_id = Number(locationId);
  }
  if (zoneId != null) {
    payload.zone_id = Number(zoneId);
  }

  return payload;
}

export function filterSelectableRows(rows) {
  return dedupeSelectedPackagingRows(rows.filter(isRowSelectable));
}

export function parsePackagingTransferResponse(data) {
  const transferred = Array.isArray(data?.transferred) ? data.transferred : [];
  const failed = Array.isArray(data?.failed) ? data.failed : [];
  const movedCount = transferred.filter((item) => !item?.skipped).length;

  return {
    transferred,
    failed,
    movedCount,
  };
}

export async function transferPackagedItemByBarcode(barcodes, destination) {
  const location = dropdownForPayload(destination.location);
  if (!location?.value) {
    return {
      success: false,
      data: { message: "Location is required." },
    };
  }

  const normalizedBarcodes = [
    ...new Set(
      (Array.isArray(barcodes) ? barcodes : [barcodes])
        .map((barcode) => String(barcode ?? "").trim())
        .filter(Boolean),
    ),
  ];

  if (normalizedBarcodes.length === 0) {
    return {
      success: false,
      data: { message: "At least one barcode is required." },
    };
  }

  const payload = {
    barcodes: normalizedBarcodes,
    location_id: location.value,
  };

  const zone = dropdownForPayload(destination.zone);
  if (!zone?.value) {
    return {
      success: false,
      data: { message: "Zone is required." },
    };
  }

  payload.zone_id = zone.value;

  return makePostApiCall("transferPackagedItemByBarcode", payload);
}

export async function deleteMultiplePackaging(packagingRecords) {
  return makePostApiCall("deleteMultiplePackaging", {
    packaging_records: packagingRecords.map(({ id, type }) => ({ id, type })),
  });
}

export function normalizePackagingSubItemRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }

  return {
    ...row,
    barcode: row.barcode ?? row.shipping_barcode,
  };
}

export function normalizePackagingSubItemRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map(normalizePackagingSubItemRow);
}

export function formatLocationZoneLabel(row) {
  const locationLabel = row?.location?.label;
  const zoneLabel = row?.zone?.label;

  if (locationLabel && zoneLabel) {
    return `${locationLabel} / ${zoneLabel}`;
  }
  if (locationLabel) {
    return locationLabel;
  }
  return "—";
}
